import React, { createContext, useContext, useState, useCallback } from "react";
import { useSiteSelector } from "@/hooks/useSiteSelector";

interface GlobalAgentState {
  isOpen: boolean;
  isMinimized: boolean;
  /** Session active = connexion voix ouverte avec le backend (WebSocket). Quand on minimise, on appelle endSession() donc la session est fermée. Utilisé en interne par le voice agent. */
  isSessionActive: boolean;
}

interface GlobalAgentContextValue {
  state: GlobalAgentState;
  openAgent: () => void;
  closeAgent: () => void;
  toggleAgent: () => void;
  minimizeAgent: () => void;
  restoreAgent: () => void;
  setSessionActive: (active: boolean) => void;
  siteId: string | undefined;
  siteName: string | undefined;
}

const GlobalAgentContext = createContext<GlobalAgentContextValue | null>(null);

export function GlobalAgentProvider({ children }: { children: React.ReactNode }) {
  const { selectedSite } = useSiteSelector();
  const [state, setState] = useState<GlobalAgentState>({
    isOpen: true,       // Always open (pill always visible)
    isMinimized: true,  // Minimized by default (shows as pill)
    isSessionActive: false,
  });

  const openAgent = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: true, isMinimized: false }));
  }, []);

  const closeAgent = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: false, isMinimized: false, isSessionActive: false }));
  }, []);

  const toggleAgent = useCallback(() => {
    setState(prev => {
      if (prev.isMinimized) {
        return { ...prev, isMinimized: false };
      }
      return { ...prev, isOpen: !prev.isOpen };
    });
  }, []);

  const minimizeAgent = useCallback(() => {
    setState(prev => ({ ...prev, isMinimized: true }));
  }, []);

  const restoreAgent = useCallback(() => {
    setState(prev => ({ ...prev, isMinimized: false }));
  }, []);

  const setSessionActive = useCallback((active: boolean) => {
    setState(prev => ({ ...prev, isSessionActive: active }));
  }, []);

  return (
    <GlobalAgentContext.Provider
      value={{
        state,
        openAgent,
        closeAgent,
        toggleAgent,
        minimizeAgent,
        restoreAgent,
        setSessionActive,
        siteId: selectedSite?.id,
        siteName: selectedSite?.name,
      }}
    >
      {children}
    </GlobalAgentContext.Provider>
  );
}

export function useGlobalAgent() {
  const context = useContext(GlobalAgentContext);
  if (!context) {
    throw new Error("useGlobalAgent must be used within GlobalAgentProvider");
  }
  return context;
}
